import { booleanAttribute, ChangeDetectionStrategy, Component, input, model, ViewEncapsulation } from '@angular/core';
import { AraButtonColor } from '../ara-button/ara-button.component';

/**
 * araButtonToggleGroup — a segmented control (like Material's `mat-button-toggle-group`).
 *
 * Container that owns the selected value; the segments are native buttons carrying the
 * `araButtonToggle` component (see AraButtonToggleComponent). Single-select by default;
 * set `multiple` for a multi-select set (value becomes an array).
 *
 * ```html
 * <ara-button-toggle-group [(value)]="view">
 *   <button araButtonToggle value="list"><mat-icon>list</mat-icon> List</button>
 *   <button araButtonToggle value="grid"><mat-icon>grid_view</mat-icon> Grid</button>
 * </ara-button-toggle-group>
 *
 * <ara-button-toggle-group [(value)]="styles" multiple>
 *   <button araButtonToggle value="bold">B</button>
 *   <button araButtonToggle value="italic">I</button>
 * </ara-button-toggle-group>
 * ```
 */
@Component({
	selector: 'ara-button-toggle-group',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	template: '<ng-content></ng-content>',
	styleUrl: './ara-button-toggle-group.component.scss',
	host: {
		class: 'ara-btn-toggle-group',
		role: 'group',
		'[class.ara-btn-toggle-group--primary]': "color() === 'primary'",
		'[class.ara-btn-toggle-group--secondary]': "color() === 'secondary'",
		'[class.ara-btn-toggle-group--success]': "color() === 'success'",
		'[class.ara-btn-toggle-group--warn]': "color() === 'warn'",
		'[class.ara-btn-toggle-group--error]': "color() === 'error'",
		'[class.ara-btn-toggle-group--disabled]': 'disabled()',
	},
})
export class AraButtonToggleGroupComponent {
	/** Selected value — a single value, or an array when `multiple` is set. Two-way: `[(value)]`. */
	readonly value = model<unknown>();
	/** Allow more than one segment selected at a time. */
	readonly multiple = input(false, { transform: booleanAttribute });
	/** Disable the whole group. */
	readonly disabled = input(false, { transform: booleanAttribute });
	/** Colour theme (shared with araButton); defaults to the same neutral `secondary` as araButton. */
	readonly color = input<AraButtonColor>('secondary');

	isSelected(candidate: unknown): boolean {
		const current = this.value();
		if (this.multiple()) {
			return Array.isArray(current) && current.includes(candidate);
		}
		return current === candidate;
	}

	toggle(candidate: unknown): void {
		if (this.disabled()) {
			return;
		}
		if (this.multiple()) {
			const current = Array.isArray(this.value()) ? [...(this.value() as unknown[])] : [];
			const index = current.indexOf(candidate);
			if (index >= 0) {
				current.splice(index, 1);
			} else {
				current.push(candidate);
			}
			this.value.set(current);
		} else {
			this.value.set(candidate);
		}
	}
}
